import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { Card, Typography, Button, List, Space, Tag, Divider, message, Row, Col, Alert, Spin, Checkbox } from 'antd';
import { 
  ArrowLeftOutlined, 
  DollarOutlined, 
  LockOutlined, 
  SafetyOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import leadPurchasesService from '../../services/leadPurchases';
import promoCodesService from '../../services/promoCodes';
import { useSelector } from 'react-redux';

const stripePromise = loadStripe('pk_live_51PiqT2LYA1pVnjq6i2lGHznNj4x71ty0VdaJFoPVD3L6CUYvAdq6xrK7sAYTmhJXIO0Qyzj3VlpfD67fGjatEmB700vcbR2RTz');
//const stripePromise = loadStripe('pk_test_r3CWfIV9ITtJWo4wD8EWdPtH');

const FreeCheckoutForm = ({ onSuccess, onError, total, isAgreed, setIsAgreed, isProcessing, setIsProcessing, currentUser }) => {
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!isAgreed) {
      message.error('Please accept the terms and conditions before proceeding');
      return;
    }

    // Check remaining daily purchases
    if (currentUser.remaining_daily_purchases <= 0) {
      message.error('You have reached your daily purchase limit. Please try again tomorrow.');
      return;
    }

    setIsProcessing(true);

    try {
      // For free purchases, no payment method is needed
      await onSuccess(null);
    } catch (error) {
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 24 }}>
        <Checkbox
          checked={isAgreed}
          onChange={(e) => setIsAgreed(e.target.checked)}
        >
          I agree to the{' '}
          <Typography.Link
            onClick={(e) => {
              e.preventDefault();
              window.open('/terms', '_blank');
            }}
          >
            terms
          </Typography.Link>
          {' '}and{' '}
          <Typography.Link
            onClick={(e) => {
              e.preventDefault();
              window.open('/terms-of-service', '_blank');
            }}
          >
            terms of service
          </Typography.Link>
          {' '}for purchasing leads
        </Checkbox>
      </div>

      <Button
        type="primary"
        htmlType="submit"
        block
        loading={isProcessing}
        disabled={isProcessing || !isAgreed}
        icon={<DollarOutlined />}
        size="large"
      >
        {isProcessing ? 'Processing...' : 'Get Free Leads'}
      </Button>
    </form>
  );
};

const CheckoutForm = ({ onSuccess, onError, total, promoCode, setPromoCode, isPromoApplied, promoCodeData }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isCardComplete, setIsCardComplete] = React.useState(false);
  const [isAgreed, setIsAgreed] = React.useState(false);
  const [cardError, setCardError] = React.useState(null);
  const { currentUser } = useSelector((state) => state.user);

  // Check if this is a completely free purchase (no payment needed)
  const isCompletelyFree = isPromoApplied && promoCodeData?.type === 'free';

  // Calculate final total based on promo code
  const calculateFinalTotal = () => {
    if (!isPromoApplied || !promoCodeData) {
      return total;
    }

    switch (promoCodeData.type) {
      case 'free':
        return 0;
      case 'percentage':
        return total * (1 - promoCodeData.value / 100);
      case 'fixed':
        return Math.max(0, total - promoCodeData.value);
      default:
        return total;
    }
  };

  const finalTotal = calculateFinalTotal();
  const discountAmount = total - finalTotal;

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!isAgreed) {
      message.error('Please accept the terms and conditions before proceeding');
      return;
    }

    // Only require card completion if not a completely free purchase
    if (!isCompletelyFree && !isCardComplete) {
      message.error('Please complete card details before purchase.');
      return;
    }

    // Check remaining daily purchases
    if (currentUser.remaining_daily_purchases <= 0) {
      message.error('You have reached your daily purchase limit. Please try again tomorrow.');
      return;
    }

    setIsProcessing(true);

    try {
      let paymentMethodId = null;
      
      // Only create payment method if not a completely free purchase and Stripe is available
      if (!isCompletelyFree && stripe && elements) {
        const cardElement = elements.getElement(CardElement);
        const { paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        });
        paymentMethodId = paymentMethod.id;
      }

      await onSuccess(paymentMethodId);
    } catch (error) {
      onError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setIsCardComplete(event.complete);
    setCardError(event.error ? event.error.message : null);
  };

  return (
    <form onSubmit={handleSubmit}>
      {!isCompletelyFree && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>Card Information</Typography.Text>
          </div>
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
            onChange={handleCardChange}
            disabled={isProcessing}
          />
          {cardError && (
            <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
              {cardError}
            </Typography.Text>
          )}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <Checkbox
          checked={isAgreed}
          onChange={(e) => setIsAgreed(e.target.checked)}
        >
          I agree to the{' '}
          <Typography.Link
            onClick={(e) => {
              e.preventDefault();
              window.open('/terms', '_blank');
            }}
          >
            terms
          </Typography.Link>
          {' '}and{' '}
          <Typography.Link
            onClick={(e) => {
              e.preventDefault();
              window.open('/terms-of-service', '_blank');
            }}
          >
            terms of service
          </Typography.Link>
          {' '}for purchasing leads
        </Checkbox>
      </div>

      <Button
        type="primary"
        htmlType="submit"
        block
        loading={isProcessing}
        disabled={isProcessing || (!isCompletelyFree && (!stripe || !isCardComplete)) || !isAgreed}
        icon={<DollarOutlined />}
        size="large"
      >
        {isProcessing ? 'Processing Payment...' : isCompletelyFree ? 'Get Free Leads' : `Pay $${finalTotal.toFixed(2)}`}
      </Button>
    </form>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, getTotal, clearCart } = useCart();
  const { currentUser } = useSelector((state) => state.user);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [paymentStatus, setPaymentStatus] = React.useState('idle'); // 'idle' | 'processing' | 'success' | 'error'
  const [promoCode, setPromoCode] = React.useState('');
  const [isPromoApplied, setIsPromoApplied] = React.useState(false);
  const [promoError, setPromoError] = React.useState('');
  const [promoLoading, setPromoLoading] = React.useState(false);
  const [isAgreed, setIsAgreed] = React.useState(false);
  const [promoCodeData, setPromoCodeData] = React.useState(null);

  // Calculate total
  const total = cart.reduce((sum, item) => sum + (parseFloat(item.lead_price) || 0), 0);

  // Calculate final total based on promo code
  const calculateFinalTotal = () => {
    if (!isPromoApplied || !promoCodeData) {
      return total;
    }

    switch (promoCodeData.type) {
      case 'free':
        return 0;
      case 'percentage':
        return total * (1 - promoCodeData.value / 100);
      case 'fixed':
        return Math.max(0, total - promoCodeData.value);
      default:
        return total;
    }
  };

  const finalTotal = calculateFinalTotal();
  const discountAmount = total - finalTotal;

  const handlePromoCodeApply = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code');
      return;
    }
    
    setPromoLoading(true);
    try {
      const data = await promoCodesService.validate(promoCode);

      if (data.valid) {
        setIsPromoApplied(true);
        setPromoCodeData(data.promo_code);
        setPromoError('');
        message.success('Promo code applied! Your purchase is now free.');
      } else {
        setIsPromoApplied(false);
        setPromoCodeData(null);
        setPromoError(data.message || 'Invalid promo code');
        message.error(data.message || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Error validating promo code:', error);
      setIsPromoApplied(false);
      setPromoCodeData(null);
      
      // Show more friendly error messages
      let errorMessage = 'Invalid promo code';
      
      if (error.response?.data?.message) {
        // Use the specific error message from the backend
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Handle network or other errors
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
          errorMessage = 'Unable to validate promo code. Please check your connection and try again.';
        } else {
          errorMessage = 'Unable to validate promo code. Please try again.';
        }
      }
      
      setPromoError(errorMessage);
      message.error(errorMessage);
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePromoCodeRemove = () => {
    setIsPromoApplied(false);
    setPromoCode('');
    setPromoError('');
    setPromoCodeData(null);
    message.info('Promo code removed');
  };

  // Check if user has reached daily limit
  if (currentUser.remaining_daily_purchases <= 0) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <Alert
          message="Daily Purchase Limit Reached"
          description={`You have reached your daily purchase limit of ${currentUser.daily_purchase_limit} leads. You have purchased ${currentUser.today_purchase_count} leads today. Please try again tomorrow.`}
          type="warning"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate('/leads')}>
              Return to Leads
            </Button>
          }
        />
      </div>
    );
  }

  const handlePaymentSuccess = async (paymentMethodId) => {
    setPaymentStatus('processing');
    try {
      const payload = {
        leadIds: cart.map(lead => lead.id),
        is_agreed: 1,
        promoCode: isPromoApplied ? promoCode : null
      };

      // Only include payment method if not a completely free purchase
      // Free promo codes don't need payment method, but percentage/fixed discounts still do
      const isCompletelyFree = isPromoApplied && promoCodeData?.type === 'free';
      if (!isCompletelyFree && paymentMethodId) {
        payload.paymentMethodId = paymentMethodId;
      }
      
      console.log('Sending payload:', payload); // Debug log
      
      // Process all leads in the cart in a single request
      await leadPurchasesService.bulkPurchaseLeads(payload);
      
      setPaymentStatus('success');
      message.success(isCompletelyFree ? 'Free leads successfully claimed!' : 'Payment successful! All leads have been purchased.');
      clearCart();
      navigate('/purchases');
    } catch (error) {
      console.error('Purchase error:', error); // Debug log
      setPaymentStatus('error');
      message.error('Purchase failed: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePaymentError = (error) => {
    setPaymentStatus('error');
    message.error('Payment failed: ' + error);
  };

  if (cart.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Typography.Title level={2}>Your cart is empty</Typography.Title>
        <Button type="primary" onClick={() => navigate('/leads')}>
          Browse Leads
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/leads')}
        style={{ marginBottom: 24 }}
      >
        Back to Leads
      </Button>

      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <Row gutter={24}>
          <Col xs={24} md={16}>
            <Card 
              title={
                <Space>
                  <LockOutlined />
                  <span>Secure Checkout</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Alert
                message="Secure Payment"
                description="Your payment information is encrypted and secure. We use Stripe to process all payments."
                type="info"
                showIcon
                icon={<SafetyOutlined />}
                style={{ marginBottom: 24 }}
              />

              {/* Promo Code Section */}
              <div style={{ marginBottom: 24 }}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Promo Code (Optional)
                </Typography.Text>
                <Space.Compact style={{ width: '100%' }}>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '6px 0 0 6px',
                      fontSize: '14px'
                    }}
                    disabled={isPromoApplied}
                  />
                  {!isPromoApplied ? (
                    <Button 
                      onClick={handlePromoCodeApply}
                      style={{ borderRadius: '0 6px 6px 0' }}
                    >
                      Apply
                    </Button>
                  ) : (
                    <Button 
                      onClick={handlePromoCodeRemove}
                      style={{ borderRadius: '0 6px 6px 0' }}
                      danger
                    >
                      Remove
                    </Button>
                  )}
                </Space.Compact>
                {promoError && (
                  <Typography.Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                    {promoError}
                  </Typography.Text>
                )}
                {isPromoApplied && (
                  <Typography.Text type="success" style={{ display: 'block', marginTop: 8 }}>
                    {promoCodeData?.type === 'free' 
                      ? '✓ Promo code applied! Your purchase is free.'
                      : '✓ Promo code applied! Discount has been applied to your purchase.'
                    }
                  </Typography.Text>
                )}
              </div>
              
              {paymentStatus === 'processing' ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                  <Typography.Title level={4} style={{ marginTop: 16 }}>
                    Processing Your {isPromoApplied && promoCodeData?.type === 'free' ? 'Free Purchase' : 'Payment'}
                  </Typography.Title>
                  <Typography.Text type="secondary">
                    Please wait while we process your {isPromoApplied && promoCodeData?.type === 'free' ? 'free purchase' : 'payment'}. Do not close this window.
                  </Typography.Text>
                </div>
              ) : isPromoApplied && promoCodeData?.type === 'free' ? (
                // For free purchases, render form without Stripe Elements
                <FreeCheckoutForm
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                  total={total}
                  isAgreed={isAgreed}
                  setIsAgreed={setIsAgreed}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  currentUser={currentUser}
                />
              ) : (
                // For paid purchases or discount purchases, render form with Stripe Elements
                <Elements stripe={stripePromise}>
                  <CheckoutForm
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    total={total}
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                    isPromoApplied={isPromoApplied}
                    promoCodeData={promoCodeData}
                  />
                </Elements>
              )}

              <Divider />

              <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Space direction="vertical" size={16}>
                  <Space>
                    <img 
                      src="https://stripe.com/img/v3/home/social.png" 
                      alt="Stripe" 
                      style={{ height: 24 }}
                    />
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8,
                      padding: '4px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px'
                    }}>
                      <SafetyCertificateOutlined style={{ color: '#1890ff', fontSize: 20 }} />
                      <Typography.Text strong>SSL Secured</Typography.Text>
                    </div>
                  </Space>
                  <Typography.Text type="secondary">
                    <SafetyOutlined /> Your payment is secured by SSL encryption
                  </Typography.Text>
                </Space>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card title="Order Summary" style={{ marginBottom: 24 }}>
              <List
                dataSource={cart}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${item.first_name} ${item.last_name}`}
                      description={item.state_name}
                    />
                    <Tag color="blue">${item.lead_price}</Tag>
                  </List.Item>
                )}
              />
              <Divider />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Typography.Text>Subtotal:</Typography.Text>
                <Typography.Text>${total}</Typography.Text>
              </div>
              {isPromoApplied && promoCodeData && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <Typography.Text type="success">Promo Code Discount:</Typography.Text>
                  <Typography.Text type="success">
                    -${discountAmount.toFixed(2)}
                  </Typography.Text>
                </div>
              )}
              <Divider />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                <Typography.Text strong>Total:</Typography.Text>
                <Typography.Title level={4} style={{ margin: 0, color: finalTotal === 0 ? '#52c41a' : 'inherit' }}>
                  {finalTotal === 0 ? 'FREE' : `$${finalTotal.toFixed(2)}`}
                </Typography.Title>
              </div>
            </Card>

            <Card>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Typography.Text>Secure Payment Processing</Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
                  <Typography.Text>SSL Encrypted Checkout</Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <GlobalOutlined style={{ color: '#722ed1' }} />
                  <Typography.Text>Instant Lead Access</Typography.Text>
                </div>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CreditCardOutlined style={{ color: '#fa8c16' }} />
                  <Typography.Text>Multiple Payment Methods</Typography.Text>
                </div> */}
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CheckoutPage; 